import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, FileBox, CheckCircle, AlertCircle, Trash2, Package, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { uploadCompressedObjAsset } from '../../../services/objAssetService';

// OBJ file parser - extracts vertex/face counts for preview validation
const parseObjStats = (text) => {
  const lines = text?.split('\n');
  let vertices = 0;
  let faces = 0;
  let objects = 0;
  let materials = [];

  for (const line of lines) {
    const trimmed = line?.trim();
    if (trimmed?.startsWith('v ')) vertices++;
    else if (trimmed?.startsWith('f ')) faces++;
    else if (trimmed?.startsWith('o ') || trimmed?.startsWith('g ')) objects++;
    else if (trimmed?.startsWith('usemtl ')) {
      const mat = trimmed?.replace('usemtl ', '')?.trim();
      if (mat && !materials?.includes(mat)) materials?.push(mat);
    }
  }

  return { vertices, faces, objects: Math.max(objects, 1), materials };
};

// Validate OBJ file content
const validateObjFile = async (file) => {
  return new Promise((resolve) => {
    if (!file.name.toLowerCase().endsWith('.obj')) {
      resolve({ valid: false, error: 'Le fichier doit avoir l\'extension .obj' });
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      resolve({ valid: false, error: 'Fichier trop volumineux (max 200 MB)' });
      return;
    }
    if (file.size === 0) {
      resolve({ valid: false, error: 'Le fichier est vide' });
      return;
    }

    const reader = new FileReader();
    // Read first 50KB to validate structure
    const blob = file.slice(0, 51200);
    reader.onload = (e) => {
      const text = e.target?.result || '';
      const hasVertices = /^v\s/m.test(text);
      const hasFaces = /^f\s/m.test(text);

      if (!hasVertices) {
        resolve({ valid: false, error: 'Fichier OBJ invalide: aucun sommet (vertex) détecté' });
        return;
      }

      const stats = parseObjStats(text);
      resolve({ valid: true, stats, previewText: text.slice(0, 200) });
    };
    reader.onerror = () => resolve({ valid: false, error: 'Impossible de lire le fichier' });
    reader.readAsText(blob);
  });
};

// Validate GLB file (binary GLTF)
const validateGlbFile = async (file) => {
  return new Promise((resolve) => {
    const nameLower = file.name.toLowerCase();
    if (!nameLower.endsWith('.glb') && !nameLower.endsWith('.gltf')) {
      resolve({ valid: false, error: 'Le fichier doit avoir l\'extension .glb ou .gltf' });
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      resolve({ valid: false, error: 'Fichier trop volumineux (max 500 MB)' });
      return;
    }
    if (file.size === 0) {
      resolve({ valid: false, error: 'Le fichier est vide' });
      return;
    }
    if (nameLower.endsWith('.glb')) {
      // GLB files start with magic bytes 0x46546C67 ("glTF")
      const reader = new FileReader();
      reader.onload = (e) => {
        const buf = e.target?.result;
        const view = new DataView(buf);
        const magic = view.getUint32(0, true);
        if (magic !== 0x46546C67) {
          resolve({ valid: false, error: 'Fichier GLB invalide: signature binaire incorrecte' });
          return;
        }
        const version = view.getUint32(4, true);
        const length = view.getUint32(8, true);
        resolve({ valid: true, stats: { format: 'GLB', version, size: length } });
      };
      reader.onerror = () => resolve({ valid: false, error: 'Impossible de lire le fichier' });
      reader.readAsArrayBuffer(file.slice(0, 12));
    } else {
      // GLTF is JSON — quick parse check
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result || '{}');
          if (!json.asset || !json.asset.version) {
            resolve({ valid: false, error: 'Fichier GLTF invalide: champ asset.version manquant' });
            return;
          }
          resolve({ valid: true, stats: { format: 'GLTF', version: json.asset.version, meshes: json.meshes?.length || 0 } });
        } catch {
          resolve({ valid: false, error: 'Fichier GLTF invalide: JSON malformé' });
        }
      };
      reader.onerror = () => resolve({ valid: false, error: 'Impossible de lire le fichier' });
      reader.readAsText(file.slice(0, 4096));
    }
  });
};

// Sanitize filename: normalize accents, remove special chars, replace spaces with underscores
const sanitizeFileName = (name) => {
  return (
    // collapse consecutive underscores
    // replace any remaining non-safe chars with _
    // strip diacritic marks
    // decompose accented chars (é → e + ́)
    name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-zA-Z0-9._-]/g, '_')?.replace(/_+/g, '_')?.replace(/^_|_$/g, '')
  );                  // trim leading/trailing underscores
};

const is3DFile = (file) => {
  const n = file?.name?.toLowerCase();
  return n?.endsWith('.obj') || n?.endsWith('.glb') || n?.endsWith('.gltf');
};

const getFileType = (file) => {
  const n = file?.name?.toLowerCase();
  if (n?.endsWith('.glb')) return 'glb';
  if (n?.endsWith('.gltf')) return 'gltf';
  return 'obj';
};

const ObjFileCard = ({ fileEntry, onRemove, onToggleDetails }) => {
  const statusColors = {
    pending: 'bg-yellow-50 border-yellow-200',
    validating: 'bg-blue-50 border-blue-200',
    valid: 'bg-green-50 border-green-200',
    invalid: 'bg-red-50 border-red-200',
    uploading: 'bg-blue-50 border-blue-200',
    uploaded: 'bg-emerald-50 border-emerald-200',
    error: 'bg-red-50 border-red-200',
  };

  const statusIcons = {
    pending: <Loader size={16} className="text-yellow-500 animate-spin" />,
    validating: <Loader size={16} className="text-blue-500 animate-spin" />,
    valid: <CheckCircle size={16} className="text-green-500" />,
    invalid: <AlertCircle size={16} className="text-red-500" />,
    uploading: <Loader size={16} className="text-blue-500 animate-spin" />,
    uploaded: <CheckCircle size={16} className="text-emerald-500" />,
    error: <AlertCircle size={16} className="text-red-500" />,
  };

  const statusLabels = {
    pending: 'En attente',
    validating: 'Validation...',
    valid: 'Valide',
    invalid: 'Invalide',
    uploading: 'Envoi...',
    uploaded: 'Importé',
    error: 'Erreur',
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(2)} MB`;
  };

  const fileType = getFileType(fileEntry?.file);
  const typeColor = fileType === 'glb' ? 'text-emerald-500' : fileType === 'gltf' ? 'text-teal-500' : 'text-indigo-500';
  const typeLabel = fileType === 'glb' ? '.GLB' : fileType === 'gltf' ? '.GLTF' : '.OBJ';

  return (
    <div className={`rounded-lg border p-3 transition-all ${statusColors?.[fileEntry?.status] || 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center shadow-sm">
          <FileBox size={20} className={typeColor} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{fileEntry?.file?.name}</span>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded bg-white border ${typeColor}`}>{typeLabel}</span>
            <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-white border border-current">
              {statusIcons?.[fileEntry?.status]}
              <span className={
                fileEntry?.status === 'uploaded' ? 'text-emerald-600' :
                fileEntry?.status === 'valid' ? 'text-green-600' :
                fileEntry?.status === 'invalid'|| fileEntry?.status === 'error' ? 'text-red-600' : 'text-blue-600'
              }>{statusLabels?.[fileEntry?.status] || fileEntry?.status}</span>
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{formatSize(fileEntry?.file?.size)}</div>

          {/* Error message */}
          {fileEntry?.error && (
            <p className="text-xs text-red-600 mt-1">{fileEntry?.error}</p>
          )}

          {/* Upload progress */}
          {fileEntry?.status === 'uploading' && (
            <div className="mt-2 h-1.5 bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${fileEntry?.progress || 0}%` }}
              />
            </div>
          )}

          {/* Uploaded URL */}
          {fileEntry?.status === 'uploaded' && fileEntry?.url && (
            <p className="text-xs text-emerald-600 mt-1 truncate">✓ {fileEntry?.url?.split('/')?.pop()?.split('?')?.[0]}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {fileEntry?.stats && (
            <button
              onClick={() => onToggleDetails(fileEntry?.id)}
              className="p-1.5 hover:bg-white rounded-md transition-colors text-gray-500"
              title="Voir les détails"
            >
              {fileEntry?.showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
          {fileEntry?.status !== 'uploading' && (
            <button
              onClick={() => onRemove(fileEntry?.id)}
              className="p-1.5 hover:bg-white rounded-md transition-colors text-gray-400 hover:text-red-500"
              title="Supprimer"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      {/* OBJ Stats Preview */}
      {fileEntry?.showDetails && fileEntry?.stats && (
        <div className="mt-3 pt-3 border-t border-current border-opacity-20 grid grid-cols-3 gap-2">
          {fileEntry?.fileType === 'obj' ? (
            <>
              <div className="text-center bg-white rounded-md p-2">
                <div className="text-lg font-bold text-indigo-600">{fileEntry?.stats?.vertices?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Sommets</div>
              </div>
              <div className="text-center bg-white rounded-md p-2">
                <div className="text-lg font-bold text-purple-600">{fileEntry?.stats?.faces?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Faces</div>
              </div>
              <div className="text-center bg-white rounded-md p-2">
                <div className="text-lg font-bold text-blue-600">{fileEntry?.stats?.objects}</div>
                <div className="text-xs text-gray-500">Objets</div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center bg-white rounded-md p-2 col-span-2">
                <div className="text-lg font-bold text-emerald-600">{fileEntry?.stats?.format} v{fileEntry?.stats?.version}</div>
                <div className="text-xs text-gray-500">Format 3D</div>
              </div>
              {fileEntry?.stats?.meshes !== undefined && (
                <div className="text-center bg-white rounded-md p-2">
                  <div className="text-lg font-bold text-teal-600">{fileEntry?.stats?.meshes}</div>
                  <div className="text-xs text-gray-500">Maillages</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ObjAssetManager = ({ isOpen, onClose, property, onAssetsImported }) => {
  const [fileEntries, setFileEntries] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [globalMessage, setGlobalMessage] = useState(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      setFileEntries([]);
      setGlobalMessage(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const processFiles = useCallback(async (files) => {
    const modelFiles = Array.from(files)?.filter(f => is3DFile(f));
    if (modelFiles?.length === 0) {
      setGlobalMessage({ type: 'error', text: 'Aucun fichier 3D détecté. Veuillez sélectionner des fichiers .obj, .glb ou .gltf' });
      return;
    }

    const newEntries = modelFiles?.map(file => ({
      id: `${file?.name}_${Date.now()}_${Math.random()}`,
      file,
      fileType: getFileType(file),
      status: 'validating',
      stats: null,
      error: null,
      url: null,
      progress: 0,
      showDetails: false,
    }));

    setFileEntries(prev => [...prev, ...newEntries]);
    setGlobalMessage(null);

    for (const entry of newEntries) {
      let result;
      if (entry?.fileType === 'obj') {
        result = await validateObjFile(entry?.file);
      } else {
        result = await validateGlbFile(entry?.file);
      }
      setFileEntries(prev => prev?.map(e =>
        e?.id === entry?.id
          ? {
              ...e,
              status: result?.valid ? 'valid' : 'invalid',
              stats: result?.stats || null,
              error: result?.error || null,
            }
          : e
      ));
    }
  }, []);

  const handleDragEnter = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    dragCounter.current--;
    if (dragCounter?.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    processFiles(e?.dataTransfer?.files);
  }, [processFiles]);

  const handleFileInput = (e) => {
    if (e?.target?.files?.length) {
      processFiles(e?.target?.files);
      e.target.value = '';
    }
  };

  const handleRemove = (id) => {
    setFileEntries(prev => prev?.filter(e => e?.id !== id));
  };

  const handleToggleDetails = (id) => {
    setFileEntries(prev => prev?.map(e =>
      e?.id === id ? { ...e, showDetails: !e?.showDetails } : e
    ));
  };

  const validEntries = fileEntries?.filter(e => e?.status === 'valid');
  const uploadedEntries = fileEntries?.filter(e => e?.status === 'uploaded');
  const invalidEntries = fileEntries?.filter(e => e?.status === 'invalid');

  const handleUploadAll = async () => {
    if (validEntries?.length === 0) return;
    if (!property?.id) {
      setGlobalMessage({ type: 'error', text: 'Aucune propriété sélectionnée pour l\'import' });
      return;
    }

    setIsUploading(true);
    setGlobalMessage(null);

    const uploadedUrls = [];

    for (const entry of validEntries) {
      setFileEntries(prev => prev?.map(e =>
        e?.id === entry?.id ? { ...e, status: 'uploading', progress: 10 } : e
      ));

      try {
        const ext = entry?.fileType === 'glb' ? 'glb' : entry?.fileType === 'gltf' ? 'gltf' : 'obj';
        const folder = entry?.fileType === 'obj' ? 'obj-models' : 'glb-models';
        const filePath = `${folder}/${property?.id}/${Date.now()}_${sanitizeFileName(entry?.file?.name)}`;

        let publicUrl;
        let compressionStats = null;

        if (entry?.fileType === 'obj') {
          // Use compressed upload for OBJ
          let result = await uploadCompressedObjAsset(
            entry?.file,
            filePath,
            {
              onProgress: (pct) => {
                setFileEntries(prev => prev?.map(e =>
                  e?.id === entry?.id ? { ...e, progress: pct } : e
                ));
              },
            }
          );
          publicUrl = result?.publicUrl;
          compressionStats = result?.compressionStats;
        } else {
          // Direct upload for GLB/GLTF (binary, no text compression)
          setFileEntries(prev => prev?.map(e =>
            e?.id === entry?.id ? { ...e, progress: 30 } : e
          ));
          const { data, error } = await supabase?.storage
            ?.from('property-assets')
            ?.upload(filePath, entry?.file, {
              cacheControl: '31536000',
              upsert: false,
              contentType: entry?.fileType === 'glb' ? 'model/gltf-binary' : 'model/gltf+json',
            });
          if (error) throw error;
          setFileEntries(prev => prev?.map(e =>
            e?.id === entry?.id ? { ...e, progress: 90 } : e
          ));
          const { data: urlData } = supabase?.storage?.from('property-assets')?.getPublicUrl(filePath);
          publicUrl = urlData?.publicUrl;
          compressionStats = { originalSize: entry?.file?.size, compressedSize: entry?.file?.size, ratio: 0 };
        }

        setFileEntries(prev => prev?.map(e =>
          e?.id === entry?.id
            ? { ...e, status: 'uploaded', progress: 100, url: publicUrl, compressionStats }
            : e
        ));

        uploadedUrls?.push({
          id: `${entry?.fileType}_${Date.now()}_${Math.random()}`,
          url: publicUrl,
          title: entry?.file?.name?.replace(/\.(obj|glb|gltf)$/i, ''),
          type: entry?.fileType,
          added_at: new Date()?.toISOString(),
          compression_ratio: compressionStats?.ratio,
          original_size: compressionStats?.originalSize,
          compressed_size: compressionStats?.compressedSize,
        });

      } catch (err) {
        console.error('Upload error:', err);
        setFileEntries(prev => prev?.map(e =>
          e?.id === entry?.id
            ? { ...e, status: 'error', error: err?.message || 'Erreur lors de l\'envoi' }
            : e
        ));
      }
    }

    if (uploadedUrls?.length > 0 && property?.id) {
      try {
        const existingVideos = Array.isArray(property?.videos) ? property?.videos : [];
        const updatedVideos = [...existingVideos, ...uploadedUrls];

        const { error: updateError } = await supabase?.from('properties')?.update({ videos: updatedVideos })?.eq('id', property?.id);
        if (updateError) throw updateError;

        const glbCount = uploadedUrls?.filter(u => u?.type === 'glb' || u?.type === 'gltf')?.length;
        const objCount = uploadedUrls?.filter(u => u?.type === 'obj')?.length;
        const parts = [];
        if (objCount > 0) parts?.push(`${objCount} .obj`);
        if (glbCount > 0) parts?.push(`${glbCount} .glb/.gltf`);

        setGlobalMessage({
          type: 'success',
          text: `${parts?.join(' + ')} importé(s) avec succès pour la visite 3D!`,
        });

        if (onAssetsImported) onAssetsImported(uploadedUrls);
      } catch (err) {
        setGlobalMessage({
          type: 'error',
          text: `Modèles uploadés mais erreur lors de l'association: ${err?.message}`,
        });
      }
    }

    setIsUploading(false);
  };

  if (!isOpen) return null;

  const totalCount = fileEntries?.length;
  const canUpload = validEntries?.length > 0 && !isUploading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Package size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Gestionnaire d'Assets 3D (.obj / .glb)</h2>
              <p className="text-sm text-gray-500">
                {property?.title ? `Propriété: ${property?.title}` : 'Import en masse de modèles 3D'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={22} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Drag & Drop Zone */}
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
              isDragging
                ? 'border-indigo-500 bg-indigo-50 scale-[1.01]'
                : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".obj,.glb,.gltf"
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
            <div className="flex flex-col items-center gap-3">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isDragging ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                <Upload size={26} className={isDragging ? 'text-indigo-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-base font-medium text-gray-700">
                  {isDragging ? 'Déposez vos fichiers 3D ici' : 'Glissez-déposez vos fichiers 3D'}
                </p>
                <p className="text-sm text-gray-500 mt-1">ou <span className="text-indigo-600 font-medium">cliquez pour parcourir</span></p>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap justify-center">
                <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono">.obj</span>
                <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded font-mono">.glb</span>
                <span className="bg-teal-50 text-teal-600 px-2 py-0.5 rounded font-mono">.gltf</span>
                <span>•</span>
                <span>Max 500 MB par fichier</span>
                <span>•</span>
                <span>Import multiple supporté</span>
              </div>
            </div>
          </div>

          {/* Global message */}
          {globalMessage && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
              globalMessage?.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {globalMessage?.type === 'success'
                ? <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                : <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              }
              {globalMessage?.text}
            </div>
          )}

          {/* Summary bar */}
          {totalCount > 0 && (
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <span className="text-gray-500">{totalCount} fichier(s) chargé(s)</span>
              {validEntries?.length > 0 && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">{validEntries?.length} valide(s)</span>}
              {uploadedEntries?.length > 0 && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">{uploadedEntries?.length} importé(s)</span>}
              {invalidEntries?.length > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{invalidEntries?.length} invalide(s)</span>}
            </div>
          )}

          {/* File list */}
          {fileEntries?.length > 0 && (
            <div className="space-y-2">
              {fileEntries?.map(entry => (
                <ObjFileCard
                  key={entry?.id}
                  fileEntry={entry}
                  onRemove={handleRemove}
                  onToggleDetails={handleToggleDetails}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {fileEntries?.length === 0 && (
            <div className="text-center py-6 text-gray-400">
              <FileBox size={40} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun fichier sélectionné</p>
              <p className="text-xs mt-1">Glissez des fichiers .obj, .glb ou .gltf ou cliquez sur la zone ci-dessus</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {validEntries?.length > 0 && !isUploading && (
              <span>{validEntries?.length} modèle(s) prêt(s) à importer</span>
            )}
            {isUploading && <span className="text-blue-600">Import en cours...</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={onClose}
              disabled={isUploading}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
            >
              Fermer
            </Button>
            <Button
              onClick={handleUploadAll}
              disabled={!canUpload}
              className={`flex items-center gap-2 ${canUpload ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            >
              {isUploading ? (
                <><Loader size={16} className="animate-spin" /> Import en cours...</>
              ) : (
                <><Upload size={16} /> Importer {validEntries?.length > 0 ? `(${validEntries?.length})` : ''}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjAssetManager;
